import { ReactNode } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, TabPanel, TabPanels, Text } from '@chakra-ui/react';

import { TabbedSection } from '~/components/tabbed-section';
import { trackEvent } from '~/features/analytics/track-event';
import {
  AccelerateIcon,
  AnalyzeIcon,
  ArrowClockwiseIcon,
  ArrowUpIcon,
  BracketsAngleEmbedIcon,
  ChartBarIcon,
  ChartUpIcon,
  ExportIcon,
  EyeIcon,
  GearIcon,
  HeadCircuitIcon,
  HybridIcon,
  LightningIcon,
  ManageIcon,
  MeasureIcon,
  MomentumIcon,
  OptimizeIcon,
  PathIcon,
  PblAccelerateIcon,
  PlugsIcon,
  PuzzlePieceIcon,
  ScaleIcon,
  StethoscopeIcon,
  TextAlignLeftIcon,
  UserPlusIcon,
  UserTabIcon,
} from '~/features/enterprise/landing-pages/icons';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';

type ImageConfig = {
  src: string;
  /** `contain` keeps the full image visible; `side-by-side` is a flush two-column cover layout; default matches Skills LP (offset + overflow crop). */
  fit?: 'contain' | 'side-by-side' | 'default';
  layout?: 'wide' | 'tall';
  width?: number;
  top?: number;
  right?: number;
};

const IMAGE_LAYOUT_DEFAULTS = {
  wide: { width: 724, top: 59, right: -76 },
  tall: { width: 559, top: 50, right: 66 },
} as const;

/** Cohorts LP only: panel padding + taller lg box so inner row stays ~480px (border-box). */
const COHORTS_TAB_PANEL_INSET = { base: 4, lg: '23px' } as const;
const COHORTS_TAB_PANEL_HEIGHT_LG = '526px';
/** next/image layout size for Cohorts contain assets (~shared aspect ratio); display size set in CSS. */
const COHORTS_CONTAIN_IMAGE_LAYOUT = { width: 1410, height: 864 } as const;
const COHORTS_MOBILE_CONTAIN_IMAGE_HEIGHT_PX = 220;

function getImageStyles(image: ImageConfig) {
  const layout = image.layout ?? 'wide';
  const defaults = IMAGE_LAYOUT_DEFAULTS[layout];
  const width = image.width ?? defaults.width;
  const top = image.top ?? defaults.top;
  const right = image.right ?? defaults.right;
  const borderRadius = layout === 'tall' ? '8px 8px 0 0' : '8px 0 0 8px';

  return {
    width: `${width}px`,
    top: `${top}px`,
    right: `${right}px`,
    borderRadius,
    sizes: `${width}px`,
  };
}

export type SkillsIntelligenceConfig = {
  /** Default when omitted: head-circuit (Skills LP). PBL uses `sample-projects` (presentation + bars per Figma). */
  headerIconId?: 'accelerate' | 'chart-up' | 'head-circuit' | 'hybrid' | 'plugs' | 'sample-projects';
  showFootnote?: boolean;
  tabs: {
    id: string;
    iconId?: string;
    image: ImageConfig;
    /** When set, renders this image as the tab button label instead of the i18n text label. */
    logoSrc?: string;
  }[];
};

function getTabIcon(iconId: string): ReactNode {
  /** `inherit` lets icon track Tab `color` (grey.500 vs blue.500); `colorScheme` on Tabs otherwise tints icons blue. */
  const props = { color: 'inherit', height: '16px', width: '16px' };
  switch (iconId) {
    case 'analyze':
      return <AnalyzeIcon {...props} />;
    case 'arrow-clockwise':
      return <ArrowClockwiseIcon {...props} />;
    case 'arrow-up':
      return <ArrowUpIcon {...props} />;
    case 'brackets-angle-embed':
      return <BracketsAngleEmbedIcon {...props} />;
    case 'chart-bar':
      return <ChartBarIcon {...props} />;
    case 'export':
      return <ExportIcon {...props} />;
    case 'eye':
      return <EyeIcon {...props} />;
    case 'gear':
      return <GearIcon {...props} />;
    case 'lightning':
      return <LightningIcon {...props} />;
    case 'manage':
      return <ManageIcon {...props} />;
    case 'measure':
      return <MeasureIcon {...props} />;
    case 'momentum':
      return <MomentumIcon {...props} />;
    case 'optimize':
      return <OptimizeIcon {...props} />;
    case 'path':
      return <PathIcon {...props} />;
    case 'puzzle-piece':
      return <PuzzlePieceIcon {...props} />;
    case 'scale':
      return <ScaleIcon {...props} />;
    case 'stethoscope':
      return <StethoscopeIcon {...props} />;
    case 'text-align-left':
      return <TextAlignLeftIcon {...props} />;
    case 'user-plus':
      return <UserPlusIcon {...props} />;
    case 'user-tab':
      return <UserTabIcon {...props} />;
    default:
      return null;
  }
}

export function SkillsIntelligenceSection({ config, slug }: { config: SkillsIntelligenceConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.skillsIntelligence`;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);
  /** Same tabbed layout as Cohorts “Accelerate” (contain images, panel sizing). */
  const isCohortsStyleLp = slug === 'cohorts' || slug === 'human-ai-support';
  const headerIconProps = { color: 'grey.500', height: '18px', width: '18px' } as const;
  const headerIcon =
    config.headerIconId === 'accelerate' ? (
      <AccelerateIcon {...headerIconProps} />
    ) : config.headerIconId === 'chart-up' ? (
      <ChartUpIcon {...headerIconProps} />
    ) : config.headerIconId === 'hybrid' ? (
      <HybridIcon {...headerIconProps} />
    ) : config.headerIconId === 'plugs' ? (
      <PlugsIcon {...headerIconProps} />
    ) : config.headerIconId === 'sample-projects' ? (
      <PblAccelerateIcon {...headerIconProps} />
    ) : (
      <HeadCircuitIcon {...headerIconProps} />
    );

  return (
    <TabbedSection
      headerIcon={headerIcon}
      headerSubtitle={t(`${tPrefix}.subtitle`) as string}
      sectionGap={{ base: '40px', md: '65px' }}
      sectionPadding={{ base: '56px 0px', md: '64px 0px' }}
      tabsContentMaxWidth='content-max-width-md'
      headerDescription={
        <Text color='grey.500' margin='0 auto' maxWidth='843px'>
          {t(`${tPrefix}.description`)}
        </Text>
      }
      headerTitle={
        <Heading
          as='h2'
          color='grey.900'
          fontSize={{ base: '2.25rem', md: '3.25rem' }}
          fontWeight='600'
          letterSpacing='-2px'
          lineHeight='1.2'
        >
          {t(`${tPrefix}.title`)}
        </Heading>
      }
      tabs={config.tabs.map((tab) => ({
        id: tab.id,
        label: t(`${tPrefix}.tabs.${tab.id}.label`) as string,
        icon: tab.iconId ? getTabIcon(tab.iconId) : null,
      }))}
      tabsFooter={
        config.showFootnote ? (
          <Text color='grey.500' fontSize='0.875rem' lineHeight='1.5' marginTop={6} textAlign='center'>
            {t(`${tPrefix}.footnote`)}
          </Text>
        ) : undefined
      }
      onTabChange={(index) => {
        trackEvent({
          type: 'click',
          name: 'Enterprise Platform CTA Clicked',
          label: analyticsLabels.intelligenceTab,
          value: config.tabs[index].id,
        });
      }}
    >
      <TabPanels>
        {config.tabs.map((tab, index) => {
          const isFirstTab = index === 0;
          const imageLoadingProps = isFirstTab ? { priority: true } : { loading: 'eager' as const };
          const imageFitContain = tab.image.fit === 'contain';
          const imageFitSideBySide = tab.image.fit === 'side-by-side';
          const imageAlt = t(`${tPrefix}.tabs.${tab.id}.imageAlt`);
          const panelImage = (
            <Image
              alt={imageAlt}
              height={0}
              quality={90}
              sizes={getImageStyles(tab.image).sizes}
              src={tab.image.src}
              width={0}
              {...imageLoadingProps}
              style={{ width: '100%', height: 'auto' }}
            />
          );

          return (
            <TabPanel key={tab.id} padding='unset'>
              <Box
                bg='rgba(123, 162, 255, 0.1)'
                border='1px solid'
                borderColor='grey.100'
                borderRadius='8px'
                height={{ base: 'auto', lg: isCohortsStyleLp ? COHORTS_TAB_PANEL_HEIGHT_LG : '480px' }}
                overflow='hidden'
                p={isCohortsStyleLp ? COHORTS_TAB_PANEL_INSET : undefined}
                position='relative'
              >
                <Flex
                  alignItems={{ lg: 'stretch' }}
                  direction={{ base: 'column', lg: 'row' }}
                  height='100%'
                  position='relative'
                >
                  <Flex
                    bg={{ base: 'rgba(123, 162, 255, 0.13)', lg: '#E2EBFF' }}
                    borderRadius={imageFitSideBySide ? { base: '8px 8px 0 0', lg: '8px 0 0 8px' } : '12px'}
                    direction='column'
                    flexShrink={0}
                    gap={4}
                    m={imageFitSideBySide ? undefined : isCohortsStyleLp ? undefined : { base: 4, lg: '23px' }}
                    pb={imageFitSideBySide ? { base: 8, lg: 3 } : 3}
                    position='relative'
                    pt={{ base: 5, lg: 8 }}
                    px={{ base: 5, lg: 8 }}
                    width={{ base: 'auto', lg: '284px' }}
                    zIndex={1}
                  >
                    {tab.logoSrc ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        alt={t(`${tPrefix}.tabs.${tab.id}.title`) as string}
                        src={tab.logoSrc}
                        style={{
                          height: '32px',
                          maxWidth: '160px',
                          objectFit: 'contain',
                          objectPosition: 'left',
                          width: 'auto',
                        }}
                      />
                    ) : (
                      <Text color='blue.500' fontFamily='heading' fontSize='1.5rem' fontWeight='500' lineHeight='1.4'>
                        {t(`${tPrefix}.tabs.${tab.id}.title`)}
                      </Text>
                    )}
                    <Text color='grey.900' fontFamily='heading' fontSize='1rem' fontWeight='500' lineHeight='1.5'>
                      {t(`${tPrefix}.tabs.${tab.id}.description`)}
                    </Text>
                    {(() => {
                      const footnoteKey = `${tPrefix}.tabs.${tab.id}.footnote`;
                      const footnoteText = t(footnoteKey);
                      return footnoteText !== footnoteKey ? (
                        <Text
                          color='grey.900'
                          fontFamily='heading'
                          fontSize='0.75rem'
                          fontWeight='500'
                          lineHeight='1.5'
                        >
                          {footnoteText}
                        </Text>
                      ) : null;
                    })()}
                  </Flex>
                  {imageFitSideBySide ? (
                    <Box
                      borderRadius='0 8px 8px 0'
                      display={{ base: 'none', lg: 'block' }}
                      flex={1}
                      minW={0}
                      overflow='hidden'
                      position='relative'
                    >
                      <Image
                        alt={imageAlt}
                        quality={90}
                        sizes='(min-width: 1024px) 60vw, 100vw'
                        src={tab.image.src}
                        fill
                        {...imageLoadingProps}
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  ) : imageFitContain ? (
                    <Box
                      display={{ base: 'none', lg: 'block' }}
                      flex={1}
                      marginLeft={isCohortsStyleLp ? { lg: 6 } : undefined}
                      minH={{ lg: '420px' }}
                      minW={0}
                      position='relative'
                      pr={6}
                      py={6}
                    >
                      <Image
                        alt={imageAlt}
                        quality={90}
                        sizes='(min-width: 1024px) 50vw, 100vw'
                        src={tab.image.src}
                        fill
                        {...imageLoadingProps}
                        style={{ objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    (() => {
                      const imgStyles = getImageStyles(tab.image);
                      return (
                        <Box
                          borderRadius={imgStyles.borderRadius}
                          display={{ base: 'none', lg: 'block' }}
                          overflow='hidden'
                          position='absolute'
                          right={imgStyles.right}
                          top={imgStyles.top}
                          width={imgStyles.width}
                        >
                          {panelImage}
                        </Box>
                      );
                    })()
                  )}
                  {imageFitSideBySide ? (
                    <Box
                      borderRadius='0 0 8px 8px'
                      display={{ base: 'block', lg: 'none' }}
                      height='220px'
                      overflow='hidden'
                      position='relative'
                      width='100%'
                    >
                      <Image
                        alt={imageAlt}
                        quality={90}
                        sizes='100vw'
                        src={tab.image.src}
                        fill
                        {...imageLoadingProps}
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  ) : imageFitContain ? (
                    <Box
                      borderRadius='8px'
                      display={{ base: 'block', lg: 'none' }}
                      height={`${COHORTS_MOBILE_CONTAIN_IMAGE_HEIGHT_PX}px`}
                      marginTop={isCohortsStyleLp ? 4 : undefined}
                      mb={4}
                      overflow='hidden'
                      position='relative'
                      width='100%'
                    >
                      <Image
                        alt={imageAlt}
                        height={COHORTS_CONTAIN_IMAGE_LAYOUT.height}
                        quality={90}
                        sizes='100vw'
                        src={tab.image.src}
                        width={COHORTS_CONTAIN_IMAGE_LAYOUT.width}
                        {...imageLoadingProps}
                        style={{
                          height: `${COHORTS_MOBILE_CONTAIN_IMAGE_HEIGHT_PX}px`,
                          maxWidth: '100%',
                          objectFit: 'contain',
                          width: '100%',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      borderRadius={tab.image.layout === 'tall' ? '8px 8px 0 0' : '8px 0 0 8px'}
                      display={{ base: 'block', lg: 'none' }}
                      marginBottom={tab.image.layout === 'tall' ? 0 : 4}
                      marginLeft={4}
                      marginRight={tab.image.layout === 'tall' ? 4 : 0}
                      overflow='hidden'
                    >
                      <Image
                        alt={imageAlt}
                        height={0}
                        quality={90}
                        sizes='(max-width: 1024px) 100vw'
                        src={tab.image.src}
                        width={0}
                        {...imageLoadingProps}
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: tab.image.layout === 'tall' ? '280px' : undefined,
                        }}
                      />
                    </Box>
                  )}
                </Flex>
              </Box>
            </TabPanel>
          );
        })}
      </TabPanels>
    </TabbedSection>
  );
}
